// filepath: apps/web/src/features/lists/FilterControls.tsx
//
// FR-UI-LIST-1 AC-2 — filter controls for the list view.
//
// SECURITY LOAD-BEARING INVARIANT (AC-2 / FR-QRY-2 AC-3 SPA mirror):
//   The component renders one control ONLY for fields in the
//   `allowlist` passed in. The allowlist is derived by
//   `useEntityListFilter.computeAllowlist`:
//
//     allowlist = column-stored fields ∪ definition.filterable_jsonb_keys
//
//   No control is rendered for any field outside the allowlist — not
//   even for other JSONB keys present in the definition. The
//   server-side FR-QRY-2 AC-3 (HTTP 400 on undeclared JSONB key)
//   remains the authoritative boundary; the SPA mirror prevents the
//   round-trip in the first place (defense-in-depth).
//
// Per-field-type UI:
//   - text    → <input type="text">           (string / integer / float / datetime)
//   - enum    → <select>                      (options from field.values)
//   - boolean → <input type="checkbox">        (eq op against true)
//   - date    → <input type="date">            (datetime fields)
//
// INVARIANTS:
//   - INV-9: zero `dangerouslySetInnerHTML`. All server strings render
//     as text nodes.
//   - FR-UI-CORE-9 / WCAG 2.1 AA: each control has a programmatic
//     label (aria-label or wrapped <label>). Boolean controls carry
//     aria-checked semantics via the native checkbox.
//   - The component is presentational — it does NOT own the
//     filter/sort state. The parent (EntityListPage) passes the
//     active filter + mutators down via the `filter` / `addFilter` /
//     `clearFilter` props.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type {
  EntityDefinitionWire,
  EntityListFilter,
  WireFieldDefinition,
} from "./types";

export interface FilterControlsProps {
  /** Resolved entity definition (drives which fields get controls). */
  definition: EntityDefinitionWire;
  /**
   * The allowlist. Pre-computed by the parent so the component does
   * not need to know the rules — the rule of one-place derivation
   * lives in `useEntityListFilter.computeAllowlist`. Exposed for
   * tests; the component itself re-derives the same set from the
   * definition to avoid trusting caller state.
   */
  allowlist: Set<string>;
  /** Active filter expressions (URL-derived). */
  filter: EntityListFilter[];
  /** Add a filter expression (silent no-op outside the allowlist). */
  addFilter: (
    key: string,
    op: EntityListFilter["op"],
    value: EntityListFilter["value"],
  ) => void;
  /** Clear a filter expression by field name. */
  clearFilter: (key: string) => void;
}

/**
 * Derive the list of (field, kind) tuples the component should
 * render controls for. The order matches the definition's field
 * order; only fields in the allowlist are emitted (AC-2).
 */
function resolveControlFields(
  definition: EntityDefinitionWire,
  allowlist: Set<string>,
): WireFieldDefinition[] {
  const fields = definition.definition_json?.fields ?? [];
  return fields.filter((f) => allowlist.has(f.name));
}

/**
 * Resolve the localisable label for a filter control. The label is
 * the field name — server-authoritative label resolution lives in
 * the UI schema (not carried in v1 entity definitions per the
 * impact report).
 */
function fieldLabel(field: WireFieldDefinition): string {
  return field.name;
}

/**
 * Translate a field's wire type to a control kind.
 */
type ControlKind = "text" | "number" | "date" | "checkbox" | "select";
function controlKindFor(field: WireFieldDefinition): ControlKind {
  if (field.type === "enum") return "select";
  if (field.type === "boolean") return "checkbox";
  if (field.type === "integer" || field.type === "float") return "number";
  if (field.type === "datetime") return "date";
  return "text";
}

/**
 * Active filter lookup for a given field. The component reads the
 * active filter to seed the controlled inputs.
 */
function activeFilterFor(
  fieldName: string,
  filter: EntityListFilter[],
): EntityListFilter | undefined {
  return filter.find((f) => f.field === fieldName);
}

/**
 * Convert a control value to a wire-shape filter value. Strings
 * pass through; numbers are coerced via Number(); booleans become
 * the literal true/false; date inputs become ISO date strings.
 */
function toFilterValue(
  raw: string,
  kind: ControlKind,
): EntityListFilter["value"] {
  if (kind === "number") {
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  if (raw === "") return null;
  return raw;
}

/**
 * FilterControls — presentational component that renders one
 * filter control per allowlist-validated field.
 */
export function FilterControls({
  definition,
  allowlist,
  filter,
  addFilter,
  clearFilter,
}: FilterControlsProps): React.ReactElement {
  const { t } = useTranslation();
  const controlFields = resolveControlFields(definition, allowlist);

  return (
    <div
      className="entity-list-filter-controls"
      data-testid="entity-list-filter-controls"
      role="group"
      aria-label={t("lists.filter.group_label", "Filter controls")}
    >
      {controlFields.length === 0 ? null : (
        <ul className="entity-list-filter-list">
          {controlFields.map((field) => {
            const kind = controlKindFor(field);
            const active = activeFilterFor(field.name, filter);
            const label = fieldLabel(field);
            return (
              <li
                key={field.name}
                className="entity-list-filter-item"
                data-testid={`entity-list-filter-item-${field.name}`}
              >
                <FilterControlField
                  field={field}
                  kind={kind}
                  label={label}
                  active={active}
                  addFilter={addFilter}
                  clearFilter={clearFilter}
                  t={t}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

interface FilterControlFieldProps {
  field: WireFieldDefinition;
  kind: ControlKind;
  label: string;
  active: EntityListFilter | undefined;
  addFilter: FilterControlsProps["addFilter"];
  clearFilter: FilterControlsProps["clearFilter"];
  t: (
    key: string,
    fallback?: string,
    interpolation?: Record<string, string | number | boolean>,
  ) => string;
}

/**
 * FilterControlField — one filter input rendered per field.
 * Encapsulates the controlled-input + submit-on-change UX so the
 * parent component stays declarative.
 */
function FilterControlField({
  field,
  kind,
  label,
  active,
  addFilter,
  clearFilter,
  t,
}: FilterControlFieldProps): React.ReactElement {
  const id = `entity-list-filter-${field.name}`;

  if (kind === "select") {
    const options = field.values ?? [];
    const currentValue =
      active?.value !== undefined && active?.value !== null
        ? String(active.value)
        : "";
    return (
      <>
        <label htmlFor={id} className="entity-list-filter-label">
          {label}
        </label>
        <select
          id={id}
          className="entity-list-filter-select"
          data-testid={`entity-list-filter-input-${field.name}`}
          value={currentValue}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") {
              clearFilter(field.name);
            } else {
              addFilter(field.name, "eq", v);
            }
          }}
        >
          <option value="">{t("lists.filter.placeholder", "Filter…")}</option>
          {options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </>
    );
  }

  if (kind === "checkbox") {
    const currentValue =
      active?.value === true ? true : active?.value === false ? false : null;
    return (
      <>
        <label htmlFor={id} className="entity-list-filter-label">
          {label}
        </label>
        <input
          id={id}
          type="checkbox"
          className="entity-list-filter-checkbox"
          data-testid={`entity-list-filter-input-${field.name}`}
          checked={currentValue === true}
          onChange={(e) => {
            if (e.target.checked) {
              addFilter(field.name, "eq", true);
            } else {
              clearFilter(field.name);
            }
          }}
        />
      </>
    );
  }

  const inputType =
    kind === "number" ? "number" : kind === "date" ? "date" : "text";
  const currentRaw =
    active?.value === null || active?.value === undefined
      ? ""
      : String(active.value);

  return (
    <>
      <label htmlFor={id} className="entity-list-filter-label">
        {label}
      </label>
      <input
        id={id}
        type={inputType}
        className="entity-list-filter-input"
        data-testid={`entity-list-filter-input-${field.name}`}
        value={currentRaw}
        onChange={(e) => {
          const v = toFilterValue(e.target.value, kind);
          if (v === null || v === "") {
            clearFilter(field.name);
          } else {
            addFilter(field.name, "eq", v);
          }
        }}
        aria-label={t("lists.filter.aria_input", "Filter {{field}}", {
          field: field.name,
        })}
      />
    </>
  );
}
